import { Box, Grid, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useEffect, useState } from "react";

import MappingItems from "./MappingItems";
import AgendaModal from "../../molecules/expert-project/agenda-modal";
import { BoxFlex } from "../boxSpaceBtw";
import {
  arrowIcons,
  componentContainerStyle,
  gridItemStyle,
  titleContainerStyles,
  titleTypography,
  arrowIconContainer,
} from "./style";

import { useStep } from "usehooks-ts";
import { calculateSteps, getEndPoint, getStartPoint } from "./helper";
import { Item } from "../../organisms/project-detail/mapping/helper";
import { TExpert_Invitation } from "../../constants/expert-project.constants";

type Props = {
  title: string;
  titleColor: string;
  componentColor: string;
  items: Item[];
  titleItemColor?: string;
  isPeMapping?: boolean;
  isScheduled?: boolean;
  expert_invitation_count?: number;
  projectId: string | null;
};

const MappingContainer = ({
  title,
  titleColor,
  componentColor,
  titleItemColor,
  items,
  isPeMapping,
  isScheduled,
  projectId,
}: Props) => {
  const totalSteps = calculateSteps(items.length);
  const [currentStep, helpers] = useStep(totalSteps);

  const [isAgendaOpen, setIsAgendaOpen] = useState(false);
  const [peId, setPeId] = useState<number | null>(null);
  const [expertId, setExpertId] = useState<number | null>(null);
  const [expertInvitation, setExpertInvitation] = useState<TExpert_Invitation>(null);
  const [openRespondedAgenda, setOpenRespondedAgenda] = useState(false);

  const { canGoToPrevStep, canGoToNextStep, goToNextStep, goToPrevStep } = helpers;

  useEffect(() => {
    helpers.reset();
  }, [items.length]);

  const handleAgendaOpen = () => setIsAgendaOpen(true);

  const handleAgendaClose = () => {
    setIsAgendaOpen(false);
    setPeId(null);
    setExpertId(null);
    setExpertInvitation(null);
    setOpenRespondedAgenda(false);
  };

  const handleAgendaIconClick = (
    peId: number,
    expertId: number,
    invitationStatus?: TExpert_Invitation,
    isRespondedAgenda?: boolean
  ) => {
    setPeId(peId);
    setExpertId(expertId);
    setExpertInvitation(invitationStatus ?? null);
    setOpenRespondedAgenda(!!isRespondedAgenda);
    handleAgendaOpen();
  };

  const visibleItems = items.slice(getStartPoint(currentStep), getEndPoint(currentStep));

  return (
    <>
      <Grid item lg={2.4} sx={gridItemStyle}>
        {/* Title Header */}
        <Box sx={titleContainerStyles(titleColor, titleItemColor)}>
          <Box sx={arrowIconContainer}>
            {canGoToPrevStep && <ArrowBackIcon onClick={goToPrevStep} sx={arrowIcons} />}
          </Box>

          <Typography sx={titleTypography}>
            <BoxFlex sx={{ gap: "0.5rem" }}>
              <span>
                {title} {items.length > 0 ? `(${items.length})` : ""}
              </span>
            </BoxFlex>
          </Typography>

          <Box sx={arrowIconContainer}>
            {canGoToNextStep && <ArrowForwardIcon onClick={goToNextStep} sx={arrowIcons} />}
          </Box>
        </Box>

        {/* Item List */}
        <Box sx={componentContainerStyle(componentColor)}>
          {visibleItems.map((item, index) => {
            const displayName = `${getStartPoint(currentStep) + index + 1}. ${item.name}`;
            const isLastItem = index === 5;
            const isRespondedAgenda = item.agenda_status !== "Sent";

            return (
              <MappingItems
                key={item.id + "-" + item.name}
                name={displayName}
                textColor={componentColor}
                noBorder={isLastItem}
                isPeMapping={isPeMapping}
                isScheduled={isScheduled}
                status={item.status}
                expert_id={item.fk_expert!}
                pe_id={item.id}
                client_priority={item.client_priority}
                agenda_shared={item.agenda_shared}
                agenda_status={item.agenda_status}
                expert_invitation={item.expert_invitation}
                relevant_division={item.relevant_division}
                relevant_designation={item.relevant_designation}
                relevant_company={item.relevant_company}
                handle_agenda_icon={() =>
                  item.fk_expert &&
                  handleAgendaIconClick(item.id, item.fk_expert, item.expert_invitation, isRespondedAgenda)
                }
              />
            );
          })}
        </Box>

        {/* Agenda Modal */}
        {isAgendaOpen && projectId && peId && expertId && expertInvitation && (
          <AgendaModal
            isOpen={isAgendaOpen}
            handleClose={handleAgendaClose}
            projectId={+projectId}
            peId={peId}
            expertId={expertId}
            expertInvitationStatus={expertInvitation}
            refetch={() => Promise.resolve()}
            openRespondedAgenda={openRespondedAgenda}
            agendaId={null}
          />
        )}
      </Grid>
    </>
  );
};

export default MappingContainer;
